import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';

import { FlashMapComponent, FlashMapService } from 'flash-map';
import {
  MindMapVersionModel,
  MindMapVersionService,
} from '@core/services/mindmap-version.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { ThemeToggle } from '@app/components/theme-toggle/theme-toggle';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MindmapTranslations } from '../../mindmap-translations';

type WritableSignal<T> = {
  (): T;
  set: (value: T) => void;
};

type FindResult = {
  id: string;
  title: string;
  type: 'topic' | 'note';
  snippet?: string;
};

type VersionOptionsMenuPosition = {
  top: number;
  left: number;
};

type FlashMapMindMapContext = {
  mindMap?: {
    id?: number;
    workspaceId?: number;
    title?: string;
    name?: string;
  };
};

export type MapMenuAction =
  | 'back'
  | 'appearance'
  | 'versions'
  | 'duplicate'
  | 'move'
  | 'share'
  | 'invite'
  | 'decks'
  | 'present'
  | 'download'
  | 'help'
  | 'move-trash';

export type VersionMenuAction = 'restore' | 'copy';

type MapTitlePanelApi = {
  mapMenuOpen: () => boolean;
  findOpen: WritableSignal<boolean>;
  findQuery: () => string;
  findResults: () => FindResult[];
  findActiveIndex: () => number;
  hasFindQuery: () => boolean;
  onMapMenuToggle: () => void;
  onMapMenuClose: () => void;
  onMapMenuItemClick: (action: MapMenuAction) => void;
  onFindQueryInput: (event: Event) => void;
  onFindClose: () => void;
  onFindButtonClick: () => void;
  onFindResultClick: (result: FindResult, index: number) => void;
  trackByFindResult: (index: number, result: FindResult) => string;
  startPresentation?: () => void;
  presentationState?: () => { active: boolean };
};

@Component({
  selector: 'app-map-title-panel',
  standalone: true,
  imports: [CommonModule, ThemeToggle],
  templateUrl: './map-title-panel.html',
  styleUrl: './map-title-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapTitlePanelComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly versionService = inject(MindMapVersionService);
  private readonly modalService = inject(NgbModal);
  private readonly uiTranslation = inject(UiTranslationService);
  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');

  @Input() flashMap: FlashMapComponent | null = null;
  @Input() mindMapId: number | null = null;
  @Input() workspaceId: number | null = null;
  @Input() mindMapExportBlocked = false;
  @Input() mindMapShareBlocked = false;
  @Input() mindMapInviteBlocked = false;
  @Input() drawingToolsVisible = false;
  @Input() canCreateMindMap = true;
  @Input() canUpdateMindMap = true;
  @Input() canDeleteMindMap = true;
  @Input() publicViewer = false;
  @Input() showBackButton = true;
  private versionsSub: { unsubscribe: () => void } | null = null;

  @Output() menuAction = new EventEmitter<MapMenuAction>();
  @Output() mindMapExportLimitClick = new EventEmitter<void>();
  @Output() mindMapShareLimitClick = new EventEmitter<void>();
  @Output() mindMapInviteLimitClick = new EventEmitter<void>();
  @Output() drawingToolsToggle = new EventEmitter<boolean>();
  @Output() versionRowClick = new EventEmitter<MindMapVersionModel>();
  @Output() versionMenuAction = new EventEmitter<{
    action: VersionMenuAction;
    version: MindMapVersionModel;
  }>();

  @ViewChild('findInput') findInput?: ElementRef<HTMLInputElement>;
  @ViewChild('versionsPanel') versionsPanel?: ElementRef<HTMLElement>;

  readonly showVersionsList = signal(false);
  readonly loadingVersions = signal(false);
  readonly errorVersions = signal<string | null>(null);
  readonly versions = signal<MindMapVersionModel[]>([]);
  readonly selectedVersionId = signal<number | null>(null);
  readonly versionOptionsOpenId = signal<number | null>(null);
  readonly versionOptionsMenuPosition =
    signal<VersionOptionsMenuPosition | null>(null);

  readonly versionWithOptionsOpen = computed(() => {
    const versionId = this.versionOptionsOpenId();

    return versionId === null
      ? null
      : (this.versions().find((version) => version.id === versionId) ?? null);
  });

  readonly groupedVersions = computed(() => {
    const groups: Record<string, MindMapVersionModel[]> = {};

    for (const version of this.versions()) {
      const date = version.createdAt
        ? version.createdAt.slice(0, 10)
        : 'Unknown';
      groups[date] ??= [];
      groups[date].push(version);
    }

    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, versions]) => ({
        date,
        versions: [...versions].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        }),
      }));
  });

  private get flashMapContext(): FlashMapMindMapContext {
    return this.flashMap as unknown as FlashMapMindMapContext;
  }

  get effectiveMindMapId(): number | null {
    return this.mindMapId ?? this.flashMapContext.mindMap?.id ?? null;
  }

  get effectiveWorkspaceId(): number | null {
    return (
      this.workspaceId ?? this.flashMapContext.mindMap?.workspaceId ?? null
    );
  }

  get panel(): MapTitlePanelApi | null {
    return this.flashMap as unknown as MapTitlePanelApi | null;
  }

  isPresentationActive(panel: MapTitlePanelApi): boolean {
    return panel.presentationState?.().active === true;
  }

  get mapTitle(): string {
    const title =
      this.flashMapContext.mindMap?.title ?? this.flashMapContext.mindMap?.name;

    if (typeof title === 'string' && title.trim().length) {
      return title.trim();
    }

    return this.i18n.tr('fallbackMapTitle');
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.isDrawingToolsShortcut(event)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearNodeSelection();
      this.drawingToolsToggle.emit(!this.drawingToolsVisible);
      this.panel?.onMapMenuClose();
      return;
    }

    if (!this.isFindShortcut(event)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.showVersionsList()) {
      this.closeVersionsPanel();
    }

    this.openFindPanel();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Node && this.host.nativeElement.contains(target)) {
      return;
    }

    if (this.versionOptionsOpenId() !== null) {
      this.closeVersionOptionsMenu();
    }
  }

  onMenuItemClick(action: MapMenuAction): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    this.clearNodeSelection();

    if (action === 'appearance') {
      this.closeVersionsPanel();
      panel.findOpen.set(false);
      panel.onMapMenuClose();
      this.menuAction.emit(action);
      return;
    }
    if (action === 'download' && this.mindMapExportBlocked) {
      panel.onMapMenuClose();
      this.mindMapExportLimitClick.emit();
      return;
    }

    if (action === 'share' && this.mindMapShareBlocked) {
      panel.onMapMenuClose();
      this.mindMapShareLimitClick.emit();
      return;
    }

    if (action === 'invite' && this.mindMapInviteBlocked) {
      panel.onMapMenuClose();
      this.mindMapInviteLimitClick.emit();
      return;
    }

    if (action === 'present') {
      this.closeVersionsPanel();
      panel.findOpen.set(false);
      panel.onMapMenuClose();
      panel.startPresentation?.();
      this.menuAction.emit(action);
      return;
    }

    panel.onMapMenuItemClick(action);
    this.menuAction.emit(action);

    if (action === 'versions') {
      panel.onMapMenuClose();
      this.openVersionsPanel();
    }
  }

  onGoBackClick(): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    if (this.showVersionsList()) {
      this.closeVersionsPanel();
    }

    panel.onMapMenuClose();
    this.onMenuItemClick('back');
  }

  onMapOptionsButtonClick(): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    if (this.showVersionsList()) {
      this.closeVersionsPanel();
    }

    if (panel.findOpen()) {
      panel.findOpen.set(false);
    }

    this.clearNodeSelection();
    panel.onMapMenuToggle();
  }

  onDrawingToolsMenuItemClick(event?: Event): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    event?.stopPropagation();
    this.clearNodeSelection();
    this.drawingToolsToggle.emit(!this.drawingToolsVisible);
    panel.onMapMenuClose();
  }

  async onTagsMenuClick(): Promise<void> {
    const mindMapId = this.effectiveMindMapId;
    if (!mindMapId || !this.canUpdateMindMap) return;
    this.clearNodeSelection();
    this.panel?.onMapMenuClose();
    const { MindmapTagsModalComponent } = await import(
      '../mindmap-tags-modal/mindmap-tags-modal'
    );
    const modalRef = this.modalService.open(MindmapTagsModalComponent, {
      centered: true,
      backdrop: 'static',
    });
    modalRef.componentInstance.mindMapId = mindMapId;
  }

  onSearchButtonClick(): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    if (this.showVersionsList()) {
      this.closeVersionsPanel();
    }

    panel.onMapMenuClose();
    this.clearNodeSelection();
    this.openFindPanel();
  }

  private clearNodeSelection(): void {
    (
      this.flashMap as unknown as { flashMapService?: FlashMapService } | null
    )?.flashMapService?.selectClear();
  }

  openVersionsPanel(): void {
    this.showVersionsList.set(true);
    this.fetchVersions();
  }

  closeVersionsPanel(): void {
    this.showVersionsList.set(false);
    this.selectedVersionId.set(null);
    this.closeVersionOptionsMenu();

    this.versionsSub?.unsubscribe();
    this.versionsSub = null;

    this.loadingVersions.set(false);
    this.errorVersions.set(null);
  }

  onVersionsBackClick(): void {
    this.closeVersionsPanel();
    this.panel?.onMapMenuToggle();
  }

  onVersionRowClick(version: MindMapVersionModel): void {
    this.closeVersionOptionsMenu();
    this.selectedVersionId.set(version.id);
    this.versionRowClick.emit(version);
  }

  onVersionMoreOptionsClick(event: MouseEvent, versionId: number): void {
    event.preventDefault();
    event.stopPropagation();

    const isOpen = this.versionOptionsOpenId() === versionId;
    if (isOpen) {
      this.closeVersionOptionsMenu();
      return;
    }

    const button = event.currentTarget;
    if (button instanceof HTMLElement) {
      this.versionOptionsMenuPosition.set(
        this.getVersionOptionsMenuPosition(button),
      );
    }

    this.versionOptionsOpenId.set(versionId);
  }

  onVersionsPanelClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      this.closeVersionOptionsMenu();
      return;
    }

    if (
      target.closest('.versions-list-more') ||
      target.closest('.versions-list-more-menu')
    ) {
      return;
    }

    this.closeVersionOptionsMenu();
  }

  onVersionMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  closeVersionOptionsMenu(): void {
    this.versionOptionsOpenId.set(null);
    this.versionOptionsMenuPosition.set(null);
  }

  private getVersionOptionsMenuPosition(
    button: HTMLElement,
  ): VersionOptionsMenuPosition {
    const panel = this.versionsPanel?.nativeElement;
    if (!panel) {
      return { top: 0, left: 0 };
    }

    const panelRect = panel.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const menuWidth = 170;
    const menuHeight = 80;
    const viewportMargin = 8;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const opensAbove =
      buttonRect.bottom + menuHeight + viewportMargin > viewportHeight &&
      buttonRect.top - menuHeight - viewportMargin >= viewportMargin;
    const viewportTop = opensAbove
      ? Math.max(viewportMargin, buttonRect.top - menuHeight - 4)
      : Math.min(
          buttonRect.bottom + 4,
          Math.max(
            viewportMargin,
            viewportHeight - menuHeight - viewportMargin,
          ),
        );
    const viewportLeft = Math.min(
      Math.max(viewportMargin, buttonRect.right - menuWidth),
      Math.max(viewportMargin, viewportWidth - menuWidth - viewportMargin),
    );

    return {
      top: viewportTop - panelRect.top,
      left: viewportLeft - panelRect.left,
    };
  }

  onVersionMenuAction(
    action: VersionMenuAction,
    version: MindMapVersionModel,
    event: MouseEvent,
  ): void {
    event.stopPropagation();
    this.closeVersionOptionsMenu();
    this.versionMenuAction.emit({ action, version });
  }

  fetchVersions(): void {
    const mindMapId = this.effectiveMindMapId;
    const workspaceId = this.effectiveWorkspaceId;

    if (!mindMapId || !workspaceId) {
      this.loadingVersions.set(false);
      this.versions.set([]);
      this.errorVersions.set(this.i18n.tr('mapContextUnavailable'));
      return;
    }

    this.versionsSub?.unsubscribe();
    this.versionsSub = null;

    this.loadingVersions.set(true);
    this.errorVersions.set(null);
    this.versionsSub = this.versionService
      .list({ mindMapId, workspaceId })
      .subscribe({
        next: (data: MindMapVersionModel[]) => {
          this.versions.set(data);
          this.loadingVersions.set(false);
          this.versionsSub = null;
        },
        error: () => {
          this.errorVersions.set(this.i18n.tr('errorLoadingVersions'));
          this.loadingVersions.set(false);
          this.versionsSub = null;
        },
      });
  }

  getVersionAuthor(version: MindMapVersionModel): string {
    const userName = version.userName?.trim();
    return userName || this.i18n.tr('unknownAuthor');
  }

  getVersionInitials(version: MindMapVersionModel): string {
    const unknownAuthor = this.i18n.tr('unknownAuthor');
    const author = this.getVersionAuthor(version)
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .trim();

    if (!author || author === unknownAuthor) {
      return 'UN';
    }

    const parts = author.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  formatGroupDate(date: string): string {
    if (!date || date === 'Unknown') {
      return this.i18n.tr('unknownDate');
    }

    const parsedDate = new Date(`${date}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compare = new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
    );
    const diffMs = today.getTime() - compare.getTime();
    const dayInMs = 24 * 60 * 60 * 1000;

    if (diffMs === 0) {
      return this.i18n.tr('today');
    }

    if (diffMs === dayInMs) {
      return this.i18n.tr('yesterday');
    }

    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(parsedDate);
  }

  openFindPanel(): void {
    const panel = this.panel;
    if (!panel) {
      return;
    }

    panel.onMapMenuClose();
    if (!panel.findOpen()) {
      panel.findOpen.set(true);
    }

    queueMicrotask(() => this.focusFindInput());
  }

  focusFindInput(): void {
    const input = this.findInput?.nativeElement;
    if (!input) {
      return;
    }

    input.focus();
    input.select();
  }

  private isFindShortcut(event: KeyboardEvent): boolean {
    const hasShortcutModifier = event.ctrlKey || event.metaKey;
    if (!hasShortcutModifier || event.altKey || event.shiftKey) {
      return false;
    }

    return event.key === 'f' || event.key === 'F';
  }

  private isDrawingToolsShortcut(event: KeyboardEvent): boolean {
    if (
      !event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      this.isTextInputLikeTarget(event.target)
    ) {
      return false;
    }

    return event.key === 'd' || event.key === 'D';
  }

  private isTextInputLikeTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest(
        'input, textarea, select, [contenteditable="true"], .jsmind-editor',
      ),
    );
  }
}
